import { useEffect, useMemo, useRef, useState } from 'react'

import { travelDay } from '../../lib/calendar'
import { stageAnchors } from '../../lib/route'
import type { DiaryEntry, LatLon, Stage } from '../../lib/types'
import { gradLabel, grid } from './geo'
import { fitProjection, toPolygonPoints } from './projection'
import { stageTrace, stageTraces } from './routeGeoJson'
import { boundsOf } from './viewport'

interface Props {
  stages: Stage[]
  diary: Record<string, DiaryEntry>
  selectedId: string
  onSelect: (id: string) => void
  /** Blendet den Vergleich für Ansichten aus, die ausdrücklich nur eine Spur zeigen. */
  vergleiche?: boolean
}

const MIN_HEIGHT = 320
const MAX_HEIGHT = 540

/**
 * Die schematische Karte: Route, Etappenpunkte und ein Gradnetz, sonst nichts.
 *
 * Sie ist die Rückfallebene ohne Kachelarchiv, ohne WebGL und im Druck - dort
 * ist sie sogar die einzige Karte, denn eine WebGL-Fläche kommt je nach
 * Browser leer aufs Papier.
 *
 * Alles, was hier gezeichnet wird, steht in den Daten der Reise. Früher lagen
 * daneben die Umrisse von Adria und Schwarzem Meer und neun Ländernamen; die
 * stimmten für die eine Reise, für die diese App gebaut wurde, und für keine
 * zweite - auf einer Atlantikroute hätte die Karte die Adria gezeichnet und
 * "BULGARIEN" beschriftet. Verortbar bleibt sie ohne das, weil das Gitter
 * seine Gradzahlen trägt und Anfang und Ende der Reise ihren Namen (siehe
 * geo.ts).
 */
export function RouteMap({ stages, diary, selectedId, onSelect, vergleiche = true }: Props) {
  const wrapper = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: 800, height: 460 })

  useEffect(() => {
    const element = wrapper.current
    if (!element) return

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return
      const width = entry.contentRect.width
      // Eine Breite von null heisst nicht "null Punkte breit", sondern
      // "gerade nicht im Layout": genau so liegt diese Karte da, solange sie
      // nur die Druckfassung neben der echten Karte ist. Würde die Grösse
      // dann übernommen, wäre die Projektion entartet und der Ausdruck leer.
      // Stattdessen bleibt der letzte brauchbare Wert stehen - beim ersten Mal
      // also die Vorgabe, die für eine Druckseite passt.
      if (width < 1) return
      setSize({
        width,
        height: Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, width * 0.58)),
      })
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  /**
   * Alles, was in den Ausschnitt gehört.
   *
   * Nur noch die Route: früher zogen die nachgezeichneten Küstenlinien den
   * Ausschnitt mit auf, und die Reise sass kleiner darin als nötig.
   */
  const points = useMemo(() => {
    const out: LatLon[] = []
    for (const stage of stages) {
      // Start, Zwischenziele, Ziel: bei einer Runde sind Start und Ziel
      // derselbe Ort, und die Zwischenziele sind das Einzige, was sie
      // auseinanderzieht.
      out.push(...stageAnchors(stage))
      // Auch die Stützpunkte des gezeichneten Wegs: eine Strasse, die einen
      // Umweg über ein Tal nimmt, verlässt sonst den Ausschnitt und die Linie
      // liefe aus der Zeichenfläche heraus. Für einen aufgezeichneten Weg
      // gilt das erst recht - er hielt sich nicht an die Planung.
      const spuren = vergleiche
        ? stageTraces(stage, diary[stage.id])
        : [stageTrace(stage, diary[stage.id])]
      for (const spur of spuren) out.push(...spur.points)
    }
    return out
  }, [stages, diary, vergleiche])

  const project = useMemo(
    () => fitProjection(points, size.width, size.height),
    [points, size.width, size.height],
  )

  // Das Gitter kommt aus dem Umriss der Route und nicht aus einer Liste: so
  // liegt es über dem Balkan wie über dem Atlantik, und über einer Tagesetappe
  // eben zwanzig Kilometer eng.
  const netz = useMemo(() => grid(boundsOf(points)), [points])

  if (stages.length === 0) {
    return <p className="leer">Keine Etappen geplant.</p>
  }

  const erste = stages[0]
  const letzte = stages[stages.length - 1]

  return (
    <div className="karte" ref={wrapper}>
      <svg
        width={size.width}
        height={size.height}
        viewBox={`0 0 ${size.width} ${size.height}`}
        role="img"
        aria-label={`Routenkarte mit ${stages.length} Etappen`}
      >
        <rect width={size.width} height={size.height} fill="var(--atlas)" />

        {/*
          Gitter und Gradzahlen. Die Zahlen sind der Grund, aus dem das Gitter
          überhaupt etwas taugt: ohne sie wären es Striche, mit ihnen lässt
          sich die Route auf jeder anderen Karte wiederfinden - auch auf einer
          gedruckten Seite ohne Netz und ohne Kartendienst.
        */}
        {netz.lats.map((lat) => {
          const y = project(lat, 0)[1]
          return (
            <g key={`lat-${lat}`}>
              <line x1={0} x2={size.width} y1={y} y2={y} stroke="var(--rule)" strokeWidth={0.5} />
              <text x={5} y={y - 4} className="karte-grad">
                {gradLabel(lat, 'breite', netz.latStep)}
              </text>
            </g>
          )
        })}
        {netz.lons.map((lon) => {
          const x = project(0, lon)[0]
          // Am rechten Rand kippt die Beschriftung nach innen, sonst stünde
          // sie halb ausserhalb der Zeichenfläche.
          const rechts = x > size.width * 0.85
          return (
            <g key={`lon-${lon}`}>
              <line y1={0} y2={size.height} x1={x} x2={x} stroke="var(--rule)" strokeWidth={0.5} />
              <text
                x={rechts ? x - 5 : x + 5}
                y={14}
                className="karte-grad"
                textAnchor={rechts ? 'end' : 'start'}
              >
                {gradLabel(lon, 'laenge', netz.lonStep)}
              </text>
            </g>
          )
        })}

        {/*
          Dieselbe Aufteilung wie in der echten Karte, siehe basemapStyle.ts:
          Farbe und Strichstärke sagen betont gegen zurückhaltend, die
          Strichelung sagt Luftlinie gegen berechnete Strasse, und eine breite,
          blasse Hülle darunter sagt: dieser Weg ist aufgezeichnet und nicht
          gerechnet. Im Druck ist das die einzige Karte, und dort trägt der
          Unterschied besonders - auf Schwarzweiss bleibt von Farbe nichts, von
          durchgezogen gegen gestrichelt und von der Hülle alles.
        */}
        {stages.flatMap((stage) => {
          const spuren = vergleiche
            ? stageTraces(stage, diary[stage.id])
            : [stageTrace(stage, diary[stage.id])]
          const done = diary[stage.id]?.done ?? false
          const istVergleich = spuren.length > 1

          return spuren.map(({ points: spur, kind }) => {
            // Welche Linie vorn steht: im Vergleich die Aufzeichnung, sonst
            // die abgehakte Etappe. Dieselbe Regel und dieselbe Begründung wie
            // bei RouteLineProps.betont in routeGeoJson.ts - und ausdrücklich
            // nicht dasselbe wie "gefahren laut Tagebuch".
            const betont = istVergleich ? kind === 'track' : done
            const stroke = betont ? 'var(--marsala)' : 'var(--asphalt)'
            const width = betont ? 3.4 : 1.8

            if (kind !== 'air') {
              return (
                <g
                  key={`${stage.id}-${kind}`}
                  onClick={() => onSelect(stage.id)}
                  style={{ cursor: 'pointer' }}
                >
                  {kind === 'track' && (
                    <polyline
                      points={toPolygonPoints(spur, project)}
                      fill="none"
                      stroke={stroke}
                      strokeWidth={width + 5}
                      opacity={0.25}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  )}
                  <polyline
                    points={toPolygonPoints(spur, project)}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={width}
                    opacity={betont ? 1 : 0.75}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
              )
            }

            const [x1, y1] = project(stage.c1[0], stage.c1[1])
            const [x2, y2] = project(stage.c2[0], stage.c2[1])
            return (
              <line
                key={`${stage.id}-${kind}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={stroke}
                strokeWidth={width}
                strokeDasharray={betont ? '3 3' : '5 4'}
                opacity={betont ? 1 : 0.5}
                strokeLinecap="round"
                onClick={() => onSelect(stage.id)}
                style={{ cursor: 'pointer' }}
              />
            )
          })
        })}

        {/*
          Der Anfang der Reise bekommt einen eigenen, offenen Punkt: die
          Etappenpunkte darunter sitzen alle auf dem Ziel einer Etappe, der
          Start der ersten hätte sonst keinen.
        */}
        {erste && <Ortsmarke at={project(erste.c1[0], erste.c1[1])} name={erste.from} start />}

        {stages.map((stage) => {
          const [x, y] = project(stage.c2[0], stage.c2[1])
          const selected = selectedId === stage.id
          const done = diary[stage.id]?.done ?? false
          // Der Reisetag, dieselbe Zahl wie im Kopf der Etappenseite: diese
          // Karte wird auch gedruckt, und zwar auf der Übersichtsseite.
          const tag = travelDay(stages, stage)
          return (
            <g key={stage.id}>
              {/* Grosszügige, unsichtbare Trefferfläche: die Karte wird mit
                  dem Daumen bedient, nicht mit der Maus. */}
              <circle
                cx={x}
                cy={y}
                r={16}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onClick={() => onSelect(stage.id)}
                role="button"
                tabIndex={0}
                aria-label={`Tag ${String(tag)}: ${stage.from} nach ${stage.to}`}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onSelect(stage.id)
                  }
                }}
              />
              <circle
                cx={x}
                cy={y}
                r={selected ? 9 : 5.5}
                fill={done ? 'var(--marsala)' : 'var(--paper)'}
                stroke="var(--asphalt)"
                strokeWidth={1.6}
                pointerEvents="none"
              />
              {selected && (
                <text x={x + 13} y={y + 4} className="karte-pin" pointerEvents="none">
                  {tag}. {stage.to}
                </text>
              )}
            </g>
          )
        })}

        {/*
          Das Ziel der Reise steht immer da, nicht nur wenn es ausgewählt ist.
          Im Druck ist nichts ausgewählt, und ohne diese beiden Namen stünde
          auf der gedruckten Karte kein einziger Ort - seit die erfundenen
          Ländernamen weg sind.
        */}
        {letzte && letzte.id !== selectedId && (
          <Ortsmarke at={project(letzte.c2[0], letzte.c2[1])} name={letzte.to} />
        )}
      </svg>
    </div>
  )
}

/**
 * Der Name eines Endpunkts der Reise, neben seinem Punkt.
 *
 * @param start setzt zusätzlich den offenen Kreis für den Anfang der Reise.
 *   Am Ziel sitzt bereits der Etappenpunkt.
 */
function Ortsmarke({ at, name, start }: { at: [number, number]; name: string; start?: boolean }) {
  const [x, y] = at
  return (
    <g pointerEvents="none">
      {start && (
        <circle
          cx={x}
          cy={y}
          r={5.5}
          fill="var(--paper)"
          stroke="var(--asphalt)"
          strokeWidth={1.6}
        />
      )}
      {name && (
        <text x={x + 13} y={y + 4} className="karte-pin">
          {name}
        </text>
      )}
    </g>
  )
}
